/**
 * @fileoverview Appeal Timeline Utilities
 * @module appealTimeline
 * 
 * Business Context:
 * - Calculates the user's current phase in the Texas property tax appeal process
 * - Handles deadline calculations (May 15 or notice + 30 days)
 * - Provides contextual messaging based on timeline position
 * 
 * Technical Context:
 * - Pure functions for timeline logic
 * - Used by AppealTimeline component and StepsDashboardPage
 */

/**
 * The phases of the Texas property tax appeal process
 */
export type AppealPhase = 
  | 'prepare'      // Signed up, waiting for notice
  | 'file'         // Notice received, need to file protest
  | 'build_case'   // Filed, building evidence and case
  | 'hearing'      // Informal meeting is scheduled, final prep (kept as 'hearing' for backwards compatibility)
  | 'results';     // Meeting complete, record outcome

/**
 * Data needed to calculate the current appeal phase
 */
export interface AppealTimelineData {
  signupDate: Date | null;
  noticeDate: Date | null;
  protestFiled: boolean;
  evidencePacketReceived: boolean;
  hearingDate: Date | null;
  outcomeRecorded: boolean;
  /** Whether the Getting Started intro module has been completed */
  introCompleted?: boolean;
  /** Whether Step 1 (Document Your Home) has been completed */
  documentingCompleted?: boolean;
}

/**
 * Result of calculating the current phase
 */
export interface PhaseResult {
  phase: AppealPhase;
  message: string;
  subMessage?: string | undefined;
  actionLabel: string;
  actionRoute?: string | undefined;
  urgency: 'normal' | 'attention' | 'urgent';
  daysUntilDeadline?: number | undefined;
  daysUntilHearing?: number | undefined;
}

/**
 * Calculate the protest filing deadline
 * Texas rule: May 15 OR 30 days from notice date, whichever is LATER
 */
export function calculateProtestDeadline(noticeDate: Date | null, year?: number): Date | null {
  if (!noticeDate) return null;
  
  const appealYear = year || noticeDate.getFullYear();
  
  // May 15 of the appeal year
  const may15 = new Date(appealYear, 4, 15); // Month is 0-indexed, so 4 = May
  
  // 30 days from notice date
  const thirtyDaysFromNotice = new Date(noticeDate);
  thirtyDaysFromNotice.setDate(thirtyDaysFromNotice.getDate() + 30);
  
  // Return whichever is later
  return may15 > thirtyDaysFromNotice ? may15 : thirtyDaysFromNotice;
}

/**
 * Calculate days until a target date
 */
export function daysUntil(targetDate: Date | null): number | null {
  if (!targetDate) return null;
  
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  
  const target = new Date(targetDate);
  target.setHours(0, 0, 0, 0);
  
  const diffTime = target.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
}

/**
 * Format a date for display
 */
export function formatDate(date: Date | null): string {
  if (!date) return '';
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

/**
 * Format a short date for badges
 */
export function formatShortDate(date: Date | null): string {
  if (!date) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(date);
}

/**
 * Check if we're in notice season (April-May typically)
 */
export function isNoticeSeason(): boolean {
  const now = new Date();
  const month = now.getMonth(); // 0-indexed
  return month >= 3 && month <= 5; // April (3) through June (5)
}

/**
 * Get the expected notice arrival period based on current date
 */
export function getExpectedNoticeArrival(): string {
  const now = new Date();
  const month = now.getMonth();
  const year = now.getFullYear();
  
  // If it's January-March, notices expected in April-May
  if (month < 3) {
    return 'April-May';
  }
  // If it's April-June and no notice, should check mail/portal
  if (month >= 3 && month <= 5) {
    return 'now (check your mail)';
  }
  // If it's July+, it's likely too late for this year
  if (month > 5) {
    return `April-May ${year + 1}`;
  }
  return 'April-May';
}

/**
 * Calculate the current appeal phase based on user data
 */
export function calculateCurrentPhase(data: AppealTimelineData): PhaseResult {
  const { noticeDate, protestFiled, evidencePacketReceived, hearingDate, outcomeRecorded } = data;
  
  // Phase 5: Results - Meeting complete, outcome recorded
  if (outcomeRecorded) {
    return {
      phase: 'results',
      message: 'Your appeal is complete!',
      subMessage: 'Check your results and see your savings.',
      actionLabel: 'View Results',
      urgency: 'normal',
    };
  }
  
  // Phase 4: Meeting - Informal meeting is scheduled
  if (hearingDate) {
    const daysUntilHearing = daysUntil(hearingDate);
    const isPast = daysUntilHearing !== null && daysUntilHearing < 0;
    
    if (isPast) {
      return {
        phase: 'results',
        message: 'Your meeting has passed',
        subMessage: 'Record your results to see your savings!',
        actionLabel: 'Record Results',
        urgency: 'attention',
        daysUntilHearing,
      };
    }
    
    const urgency = daysUntilHearing !== null && daysUntilHearing <= 3 
      ? 'urgent' 
      : daysUntilHearing !== null && daysUntilHearing <= 7 
        ? 'attention' 
        : 'normal';
    
    return {
      phase: 'hearing',
      message: daysUntilHearing === 0 
        ? 'Your meeting is TODAY!' 
        : daysUntilHearing === 1 
          ? 'Your meeting is tomorrow!' 
          : `Your meeting is in ${daysUntilHearing} days`,
      subMessage: `Scheduled for ${formatDate(hearingDate)}. You'll meet with an appraiser.`,
      actionLabel: 'Finish Preparation',
      actionRoute: '/step/4/arguments',
      urgency,
      daysUntilHearing: daysUntilHearing ?? undefined,
    };
  }
  
  // Phase 3: Build Case - Filed, building evidence
  if (protestFiled) {
    if (evidencePacketReceived) {
      return {
        phase: 'build_case',
        message: 'Build your case',
        subMessage: 'You have the county\'s evidence. Analyze it and prepare your arguments.',
        actionLabel: 'Continue Preparation',
        actionRoute: '/step/3/find-flaws',
        urgency: 'normal',
      };
    }
    
    // Protest filed, no evidence packet, but documenting complete -> guide to flaws
    if (data.documentingCompleted) {
      return {
        phase: 'build_case',
        message: 'Appeal filed! Now build your case.',
        subMessage: 'Start analyzing the county\'s valuation for errors while waiting for the evidence packet.',
        actionLabel: 'Find County Flaws',
        actionRoute: '/step/3/find-flaws',
        urgency: 'normal',
      };
    }
    
    return {
      phase: 'build_case',
      message: 'Appeal filed! Now build your case.',
      subMessage: 'Document your home while waiting for the county\'s evidence packet (usually 2-4 weeks before your meeting).',
      actionLabel: 'Document Your Home',
      actionRoute: '/step/1/document',
      urgency: 'normal',
    };
  }
  
  // Phase 2: File - Notice received, need to file
  if (noticeDate) {
    const deadline = calculateProtestDeadline(noticeDate);
    const daysRemaining = daysUntil(deadline);
    
    // Deadline passed
    if (daysRemaining !== null && daysRemaining < 0) {
      return {
        phase: 'file',
        message: 'Filing deadline has passed',
        subMessage: 'Contact your county appraisal district for options.',
        actionLabel: 'Learn More',
        urgency: 'urgent',
        daysUntilDeadline: daysRemaining,
      };
    }
    
    const urgency = daysRemaining !== null && daysRemaining <= 3 
      ? 'urgent' 
      : daysRemaining !== null && daysRemaining <= 7 
        ? 'attention' 
        : 'normal';
    
    return {
      phase: 'file',
      message: daysRemaining !== null && daysRemaining <= 7 
        ? `File your appeal! Only ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left`
        : 'File your appeal',
      subMessage: deadline 
        ? formatDate(deadline)
        : 'Submit your appeal form to the county.',
      actionLabel: 'File Now',
      actionRoute: '/step/2/file-appeal',
      urgency,
      daysUntilDeadline: daysRemaining ?? undefined,
    };
  }
  
  // Phase 1: Prepare - No notice yet
  const expectedNotice = getExpectedNoticeArrival();
  const inNoticeSeason = isNoticeSeason();
  
  // Fresh account: guide into Getting Started intro before anything else
  if (!data.introCompleted) {
    return {
      phase: 'prepare',
      message: 'Welcome! Let\u2019s get started',
      subMessage: 'Watch two short videos to learn how the appeal process works and what to expect.',
      actionLabel: 'Getting Started',
      actionRoute: '/intro/getting-started',
      urgency: 'normal' as const,
    };
  }
  
  return {
    phase: 'prepare',
    message: inNoticeSeason 
      ? 'Check for your Notice of Appraised Value' 
      : 'Prepare for your appeal',
    subMessage: inNoticeSeason
      ? 'Notices are being mailed now. Check your mail and county portal.'
      : `Your Notice will arrive ${expectedNotice}. Document your home now to get ahead.`,
    actionLabel: 'Document Your Home',
    actionRoute: '/step/1/document',
    urgency: inNoticeSeason ? 'attention' : 'normal',
  };
}

/**
 * Get phase display info (label, description, icon name)
 */
export function getPhaseInfo(phase: AppealPhase): { label: string; description: string } {
  switch (phase) {
    case 'prepare':
      return { 
        label: 'Prepare', 
        description: 'Document your home' 
      };
    case 'file':
      return { 
        label: 'File', 
        description: 'Submit your appeal' 
      };
    case 'build_case':
      return { 
        label: 'Build Case', 
        description: 'Gather evidence' 
      };
    case 'hearing':
      return { 
        label: 'Meeting', 
        description: 'Talk with appraiser' 
      };
    case 'results':
      return { 
        label: 'Results', 
        description: 'Record outcome' 
      };
  }
}

/**
 * Get all phases in order for display
 */
export function getAllPhases(): AppealPhase[] {
  return ['prepare', 'file', 'build_case', 'hearing', 'results'];
}

/**
 * Check if a phase is complete relative to current phase
 */
export function isPhaseComplete(phase: AppealPhase, currentPhase: AppealPhase): boolean {
  const phases = getAllPhases();
  const currentIndex = phases.indexOf(currentPhase);
  const phaseIndex = phases.indexOf(phase);
  return phaseIndex < currentIndex;
}

/**
 * Check if a phase is the current one
 */
export function isCurrentPhase(phase: AppealPhase, currentPhase: AppealPhase): boolean {
  return phase === currentPhase;
}

/**
 * Check if a phase is upcoming (not yet reached)
 */
export function isUpcomingPhase(phase: AppealPhase, currentPhase: AppealPhase): boolean {
  const phases = getAllPhases();
  const currentIndex = phases.indexOf(currentPhase);
  const phaseIndex = phases.indexOf(phase);
  return phaseIndex > currentIndex;
}

/**
 * Determine if a step should be locked based on timeline data
 */
export interface StepLockStatus {
  isLocked: boolean;
  reason?: string | undefined;
  badge?: string | undefined;
}

export function getStepLockStatus(
  stepNumber: number, 
  data: AppealTimelineData
): StepLockStatus {
  const { noticeDate, protestFiled, evidencePacketReceived, hearingDate } = data;
  
  switch (stepNumber) {
    case 1: // Document Your Home - Always available
      return { isLocked: false, badge: 'Start Anytime' };
      
    case 2: // File Your Appeal
      if (protestFiled) {
        return { isLocked: false, badge: 'Filed ✓' };
      }
      if (!noticeDate) {
        return { 
          isLocked: true, 
          reason: 'Available when your notice arrives',
          badge: 'Waiting for Notice'
        };
      }
      const deadline = calculateProtestDeadline(noticeDate);
      const daysRemaining = daysUntil(deadline);
      if (deadline && daysRemaining !== null) {
        const daysText = daysRemaining === 0 
          ? 'Today!' 
          : `${daysRemaining} day${daysRemaining === 1 ? '' : 's'}`;
        return { 
          isLocked: false, 
          badge: `Due ${formatShortDate(deadline)} · ${daysText}` 
        };
      }
      return { isLocked: false, badge: deadline ? `Due ${formatShortDate(deadline)}` : undefined };
      
    case 3: // Find County Flaws
      if (!protestFiled) {
        return { 
          isLocked: true, 
          reason: 'Available after you file your appeal',
          badge: 'After Filing'
        };
      }
      if (!evidencePacketReceived) {
        return { 
          isLocked: false, // Allow access but show waiting status
          badge: 'Waiting for Evidence Packet'
        };
      }
      return { isLocked: false };
      
    case 4: // Prepare Your Case
      // Gate on protest being filed (same prerequisite as Step 3)
      if (!protestFiled) {
        return { 
          isLocked: true,
          reason: 'Available after you file your appeal',
          badge: 'After Filing'
        };
      }
      if (!hearingDate) {
        return { 
          isLocked: false, // Allow access but show meeting status (same pattern as Step 3 evidence packet)
          badge: 'Meeting Not Scheduled'
        };
      }
      const daysUntilHearing = daysUntil(hearingDate);
      if (daysUntilHearing !== null && daysUntilHearing <= 7) {
        return { 
          isLocked: false, 
          badge: `${daysUntilHearing} day${daysUntilHearing === 1 ? '' : 's'} until meeting` 
        };
      }
      return { 
        isLocked: false, 
        badge: `Before ${formatShortDate(hearingDate)}` 
      };
      
    default:
      return { isLocked: false };
  }
}
